// Spine (spine-canvas) ランタイムのラッパ。ブラウザ専用 (Vitest からは import しない)。
// POC: LUNAIRE の idle / attack を Canvas 2D 上に骨格アニメで描画する。
import {
  AssetManager, AtlasAttachmentLoader, SkeletonJson, Skeleton,
  AnimationState, AnimationStateData, SkeletonRenderer,
  Physics, TextureAtlas, RegionAttachment,
} from '@esotericsoftware/spine-canvas'
import { OVERRIDE_THEN, resolveSpineAnimation, type PlayerVisualState } from './spine_anim_map'
import type { ExecuteVariant } from './types'
import { PLAYER_DISPLAY_HEIGHT } from './balance'
import { COUNTER_FROM_DODGE_MIX, applyCounterFromDodgeBlend } from './dodge_counter_motion'

// 描画時の目標高さ (px)。 全キャラのサイズ基準 ("ものさし") = balance.PLAYER_DISPLAY_HEIGHT。
// 経緯: 256 大→88 小→140→ 2026-06-01 サイズ設計で 120 に確定 (画面縦の約22%・小さめ)。
const SPINE_TARGET_HEIGHT = PLAYER_DISPLAY_HEIGHT

interface SpineRig {
  skeleton: Skeleton
  state: AnimationState
  renderer: SkeletonRenderer
  scale: number
  currentAnim: string | null
  // 揺れ (physics 拘束・scripts/rig_soft_follow.mjs) へ渡す、前のフレームの体の位置 (画面 px) と向き。
  lastBody: PlayerBodyPose | null
}

/** 画面での体の位置 (足元・px) と向き。 揺れに「体が動いた分」を渡すために使う。 */
export interface PlayerBodyPose { x: number, y: number, facing: 1 | -1 }

let assetManager: AssetManager | null = null
let rig: SpineRig | null = null
let pendingBuild: { jsonPath: string; atlasPath: string } | null = null

/**
 * Spine スケルトンの読み込みを開始する。pathPrefix 配下に json/atlas/png がある前提。
 */
export function preloadSpinePlayer(pathPrefix: string, jsonFile: string, atlasFile: string): void {
  if (assetManager || typeof Image === 'undefined') return
  assetManager = new AssetManager(pathPrefix)
  assetManager.loadText(jsonFile)
  assetManager.loadTextureAtlas(atlasFile)
  pendingBuild = { jsonPath: jsonFile, atlasPath: atlasFile }
}

/**
 * dev 用の未追跡 WIP skeleton を、存在する時だけ読む。
 * HEAD が失敗した場合も本番 skeleton を必ず preload するので、他環境では描画待ちのままにならない。
 */
export function preloadSpinePlayerWithFallback(
  pathPrefix: string, preferredJsonFile: string, fallbackJsonFile: string, atlasFile: string,
): void {
  void fetch(`${pathPrefix}${preferredJsonFile}`, { method: 'HEAD', cache: 'no-store' })
    .then((response) => preloadSpinePlayer(pathPrefix, response.ok ? preferredJsonFile : fallbackJsonFile, atlasFile))
    .catch(() => preloadSpinePlayer(pathPrefix, fallbackJsonFile, atlasFile))
}

/** 読み込み完了時に一度だけ Skeleton/AnimationState/Renderer を構築する。 */
function tryBuild(ctx: CanvasRenderingContext2D): void {
  if (rig || !assetManager || !pendingBuild) return
  if (!assetManager.isLoadingComplete()) return

  // AssetManagerBase.get() returns AssetData — cast to the concrete types we loaded.
  const atlas = assetManager.get(pendingBuild.atlasPath) as TextureAtlas
  const skeletonJson = new SkeletonJson(new AtlasAttachmentLoader(atlas))
  const skeletonData = skeletonJson.readSkeletonData(assetManager.get(pendingBuild.jsonPath) as string)

  const skeleton = new Skeleton(skeletonData)
  const h = skeletonData.height || SPINE_TARGET_HEIGHT
  const scale = SPINE_TARGET_HEIGHT / h

  const stateData = new AnimationStateData(skeletonData)
  stateData.defaultMix = 0.12
  // 通常攻撃だけは開始輪郭をぼかさない。全体mixを変えると歩き・パリィ・致命まで巻き込むため、
  // 実在アニメを確認して個別に上書きする。戻りは少し長くし、刺した後の構え直しを残す。
  if (skeletonData.findAnimation('idle') && skeletonData.findAnimation('attack')) {
    stateData.setMix('idle', 'attack', 0.035)
    stateData.setMix('attack', 'idle', 0.080)
  }
  // 接触別アニメは attack の前半を複製しているため、命中時の切替mixは0で輪郭を保つ。
  // 終了後だけ既存attackと同じ80msで構えへ戻す。
  for (const contactAnim of ['attack_guarded', 'attack_shallow', 'attack_deep']) {
    if (!skeletonData.findAnimation(contactAnim)) continue
    if (skeletonData.findAnimation('attack')) stateData.setMix('attack', contactAnim, 0)
    if (skeletonData.findAnimation('idle')) stateData.setMix(contactAnim, 'idle', 0.080)
  }
  // 連撃の3段 (attack_1 / attack_2 / attack_3・連撃を試す間だけ使う) も旧 attack と同じ繋ぎ方。接触別は同じ前半の複製なので mix 0。
  //   段から次の段へは mix 0: 前の段の終わりの姿 = 次の段の始まりの姿 (1・2段目の終わりと2・3段目の始まりは構え直し、
  //   3段目の終わりと1段目の始まりは素立ち・scripts/author_player_attack_combo.mjs) なので、混ぜると次の段の溜めの出だしが鈍るだけ。
  const comboSteps = ['attack_1', 'attack_2', 'attack_3']
  const comboFamily = (base: string) => [base, ...['guarded', 'shallow', 'deep'].map((kind) => `${base}_${kind}`)]
    .filter((name) => skeletonData.findAnimation(name))
  for (const base of comboSteps) {
    if (!skeletonData.findAnimation(base) || !skeletonData.findAnimation('idle')) continue
    stateData.setMix('idle', base, 0.035)
    stateData.setMix(base, 'idle', 0.080)
    for (const contactAnim of comboFamily(base).slice(1)) {
      stateData.setMix(base, contactAnim, 0)
      stateData.setMix(contactAnim, 'idle', 0.080)
    }
  }
  comboSteps.forEach((base, index) => {
    const next = comboSteps[(index + 1) % comboSteps.length]
    if (!skeletonData.findAnimation(next)) return
    for (const from of comboFamily(base)) stateData.setMix(from, next, 0)
  })
  // 1・2段目は構え直しの姿で終わり、2・3段目はそこから始まる (2026-10-03)。 連撃をやめた時の戻り attack_settle は
  //   その構えから始まるので mix 0。 素立ちへは idle と同じ 80ms。 戻りの途中で次の段・歩きへ移る時は短く混ぜる。
  if (skeletonData.findAnimation('attack_settle')) {
    for (const base of comboSteps.slice(0, 2)) for (const from of comboFamily(base)) stateData.setMix(from, 'attack_settle', 0)
    if (skeletonData.findAnimation('idle')) stateData.setMix('attack_settle', 'idle', 0.080)
    for (const next of comboSteps) if (skeletonData.findAnimation(next)) stateData.setMix('attack_settle', next, 0.06)
  }
  // 受け構え (parry → guard_in → guard → guard_out) は全て「直前と同じ姿」から始まるので mix 0。
  //   混色させると、出て行く parry がその間も勝手にほどけ続けて中間フレームが動く
  //   = 姿が同じなのに「カクッ」と見える (2026-07-20 実機FBの原因)。
  //   guard → guard_out だけは呼吸のぶん (数単位) ズレうるので、ごく短い mix で吸収する。
  const guardChain: [string, string, number][] = [
    ['parry', 'guard_in', 0], ['guard_in', 'guard', 0], ['guard', 'guard_out', 0.06],
    ['guard_in', 'guard_out', 0], ['guard_out', 'idle', 0.08],
    // 致命を弾いて押される時の重い受けの姿 (DEV ?execPush=new・scripts/author_parry_heavy_from_parry.mjs)。
    //   parry → parry_heavy は当たった瞬間の parry の姿 (入りの途中のことがある) から短く混ぜる。
    //   parry_heavy の終わりの姿 = parry_heavy_out の頭の姿なので mix 0。
    ['parry', 'parry_heavy', 0.05], ['parry_heavy', 'parry_heavy_out', 0], ['parry_heavy_out', 'idle', 0.08],
    ['parry_heavy', 'idle', 0.12],
  ]
  for (const [from, to, mix] of guardChain) {
    if (skeletonData.findAnimation(from) && skeletonData.findAnimation(to)) stateData.setMix(from, to, mix)
  }
  // 見切り反撃を回避の止まり姿から始める (10-09 採用・配布版の既定・src/dodge_counter_motion.ts): 最初のキー = 回避の止まり姿なので混ぜ 0。
  //   旧の counter (DEV ?counterFromDodge=0) にはこの混ぜは掛からない (既定の 0.12 s のまま)。
  for (const [from, to, mix] of COUNTER_FROM_DODGE_MIX) {
    if (skeletonData.findAnimation(from) && skeletonData.findAnimation(to)) stateData.setMix(from, to, mix)
  }
  // 作り直した歩き (DEV ?lunaWalk=new|newslow・src/player_walk_new.ts): 立ち姿からは短く混ぜて入る (歩きの頭 = 手前の足が体の真下・
  //   混ぜが長いと、その間だけ着いた足が床を滑る)。 止まる時は脚を止まる時の1歩が受け持つので、上体だけゆっくり戻す。
  //   2 版目 (DEV ?lunaWalk=v2・walk_v2 / walk_back_v2) も同じ混ぜ方
  for (const [from, to, mix] of [['idle', 'walk_n', 0.05], ['idle', 'walk_back_n', 0.05], ['walk_n', 'idle', 0.16], ['walk_back_n', 'idle', 0.16],
    ['walk_n', 'walk_back_n', 0.08], ['walk_back_n', 'walk_n', 0.08],
    ['idle', 'walk_v2', 0.05], ['idle', 'walk_back_v2', 0.05], ['walk_v2', 'idle', 0.16], ['walk_back_v2', 'idle', 0.16],
    ['walk_v2', 'walk_back_v2', 0.08], ['walk_back_v2', 'walk_v2', 0.08],
    // 3 版目の小走り (DEV ?lunaWalk=v3・walk_v3 / walk_back_v3) も同じ混ぜ方
    ['idle', 'walk_v3', 0.05], ['idle', 'walk_back_v3', 0.05], ['walk_v3', 'idle', 0.16], ['walk_back_v3', 'idle', 0.16],
    ['walk_v3', 'walk_back_v3', 0.08], ['walk_back_v3', 'walk_v3', 0.08],
    // 4 版目 (DEV ?lunaWalk=v4run|v4walk・鍵の姿勢の走りと歩き) も同じ混ぜ方
    ['idle', 'walk_v4run', 0.05], ['idle', 'walk_back_v4run', 0.05], ['walk_v4run', 'idle', 0.16], ['walk_back_v4run', 'idle', 0.16],
    ['walk_v4run', 'walk_back_v4run', 0.08], ['walk_back_v4run', 'walk_v4run', 0.08],
    ['idle', 'walk_v4walk', 0.05], ['idle', 'walk_back_v4walk', 0.05], ['walk_v4walk', 'idle', 0.16], ['walk_back_v4walk', 'idle', 0.16],
    ['walk_v4walk', 'walk_back_v4walk', 0.08], ['walk_back_v4walk', 'walk_v4walk', 0.08],
    // 5 版目の歩き (DEV ?lunaWalk=v5・膝の曲げを鍵にした歩き) も同じ混ぜ方
    ['idle', 'walk_v5', 0.05], ['idle', 'walk_back_v5', 0.05], ['walk_v5', 'idle', 0.16], ['walk_back_v5', 'idle', 0.16],
    ['walk_v5', 'walk_back_v5', 0.08], ['walk_back_v5', 'walk_v5', 0.08],
    // 6 版目の歩き (DEV ?lunaWalk=v6a|v6b|v6c・上半身が脚に合わせて動く 3 案)・7 版目 (DEV ?lunaWalk=v7a|v7b・速さ 130・空いた手の置き場所 2 案) も同じ混ぜ方
    ...(['v6a', 'v6b', 'v6c', 'v7a', 'v7b'] as const).flatMap((v) => [
      ['idle', `walk_${v}`, 0.05], ['idle', `walk_back_${v}`, 0.05], [`walk_${v}`, 'idle', 0.16], [`walk_back_${v}`, 'idle', 0.16],
      [`walk_${v}`, `walk_back_${v}`, 0.08], [`walk_back_${v}`, `walk_${v}`, 0.08]] as const)] as const) {
    if (skeletonData.findAnimation(from) && skeletonData.findAnimation(to)) stateData.setMix(from, to, mix)
  }
  // 主人公の致命の新しい版 (DEV ?lunaFinisher=new・scripts/author_lunaire_finisher_v2.mjs): 止めの後の抜く絵の頭 = 刺した姿なので混ぜ 0 (抜く瞬間を鈍らせない)。
  if (skeletonData.findAnimation('execute_finisher_v2') && skeletonData.findAnimation('execute_finisher_v2_pull')) {
    stateData.setMix('execute_finisher_v2', 'execute_finisher_v2_pull', 0)
  }
  const state = new AnimationState(stateData)

  // Canvas backend はメッシュ非対応 (Essential カットアウトと一致)
  const renderer = new SkeletonRenderer(ctx)
  renderer.triangleRendering = false

  rig = { skeleton, state, renderer, scale, currentAnim: null, lastBody: null }
}

/** skeleton にそのアニメがあるか (読み込み前は false)。 */
export function hasSpinePlayerAnimation(name: string): boolean {
  return rig?.skeleton.data.findAnimation(name) != null
}

export function isSpinePlayerReady(): boolean {
  return rig !== null
}

/** 骨と絵の読み込みが終わったか (組み立ては初めて描く時)。読み始めていなければ待つ物は無いので true。 */
export function isSpinePlayerLoaded(): boolean {
  return rig !== null || !assetManager || assetManager.isLoadingComplete()
}

/**
 * Spine 経路で LUNAIRE を描画する。呼び出し側 (drawPlayer) は ctx を
 * 足元原点へ translate 済み・facing 反転済みで渡すこと。
 * @returns 描画できたら true。まだ読み込み中なら false。
 */
export function drawSpinePlayer(
  ctx: CanvasRenderingContext2D,
  visualState: PlayerVisualState,
  dtSec: number,
  executeVariant: ExecuteVariant = 'normal',
  overrideAnimationName?: string,
  forceRestart?: boolean,
  syncAnimationTimeSec?: number,
  body?: PlayerBodyPose,
): boolean {
  tryBuild(ctx)
  if (!rig) return false

  let animName: string
  let animLoop: boolean
  // 完走後に自動で繋ぐアニメ (guard_in → guard)。
  let queued: { name: string, loop: boolean } | undefined
  // 描画専用 override: 通常の state→anim 解決を飛び越えて、指定アニメを非ループ再生する。
  //   トドメ致命の余韻 (execute_finisher_afterglow) のように「ビートに応じて FX 側が決める」
  //   切替に使う。 ExecuteVariant に混ぜると判定/解決ロジックを汚すので描画層だけで完結させる。
  //   ※ 実在する時のみ優先。 無ければ通常解決へ落とす (アセット未書き出し時の安全側 fallback)。
  if (overrideAnimationName && rig.skeleton.data.findAnimation(overrideAnimationName)) {
    animName = overrideAnimationName
    animLoop = false
    queued = OVERRIDE_THEN[animName]
  } else {
    const resolved = resolveSpineAnimation(visualState, executeVariant)
    // 要求アニメがスケルトンに無い場合の fallback。
    //  - executing (execute / execute_finisher) が未実装の間は idle へ倒さず false を返し、
    //    呼び出し側 (render.ts) の既存 frame-by-frame 致命描画に委ねる (致命が棒立ち呼吸へ退行しない)。
    //  - それ以外 (開発 fixture に 'attack' が無い等) は idle へ逃がす。idle も無ければ諦めて false。
    animName = resolved.name
    animLoop = resolved.loop
    queued = resolved.then
    if (!rig.skeleton.data.findAnimation(animName)) {
      if (visualState === 'executing') return false
      animName = 'idle'
      animLoop = true
      if (!rig.skeleton.data.findAnimation(animName)) return false
    }
  }
  // forceRestart: 同一アニメ継続中でも track time を 0 に戻したい時 (連続被弾の hurt を頭から再生)。
  //   未指定時は現状どおり (afterglow/通常経路の挙動は不変)。
  const animationChanged = rig.currentAnim !== animName || forceRestart
  if (animationChanged) {
    const previousEntry = rig.state.getTrack(0)
    const entry = rig.state.setAnimation(0, animName, animLoop)
    // 回避 → 見切り反撃 (counter_from_dodge) を止まり姿から外れた時刻で押した時だけ 2〜3 コマ混ぜ、回避の時計を止める
    //   (10-09 夜・早めに押すと剣を持たない腕と髪が1コマで跳んだ・src/dodge_counter_motion.ts)。 他の切り替えには何もしない。
    applyCounterFromDodgeBlend(previousEntry, entry, animName)
    rig.currentAnim = animName
    // 完走後に自動で繋ぐアニメ (受け構えの入り → 保持ループ)。 トラックへ予約するだけなので
    //   以降のフレームは animationChanged=false のまま = 毎フレーム貼り直さない。
    if (queued && rig.skeleton.data.findAnimation(queued.name)) {
      rig.state.addAnimation(0, queued.name, queued.loop, 0)
    }
  }

  // 歩きの比較版 (DEV ?lunaWalk=new|newslow) だけ: 絵の時計を進んだ距離で進める (揺れの時計は dtSec のまま)。 呼ばなければ今のまま
  rig.state.update(Math.max(0, takeAnimDtOverride() ?? dtSec))
  // attack→接触別コピーは同じ前半を持つ。切替時にゲーム側のattack経過へ合わせ、
  // 新アニメのF0へ巻き戻って「二度振る」見え方になるのを防ぐ。
  if (animationChanged && syncAnimationTimeSec !== undefined) {
    const entry = rig.state.getTrack(0)
    if (entry) entry.trackTime = Math.max(0, syncAnimationTimeSec)
  }
  rig.state.apply(rig.skeleton)
  // 比較版 (DEV ?parryLook=kesaki) の髪の跳ね上げ: 次に描くコマだけ、名指しの骨の回転を足す。 呼ばなければ何もしない。
  if (boneNudge) {
    for (const [name, deg] of Object.entries(boneNudge)) { const b = rig.skeleton.findBone(name); if (b) b.pose.rotation += deg }
    boneNudge = null
  }
  if (legOverride) { applyLegOverride(rig.skeleton, legOverride); legOverride = null }
  rig.skeleton.x = 0
  rig.skeleton.y = 0
  // 人形は「左向き」で作られている。 ゲームの facing 規則(右=反転なし)に合わせるため
  // 基準を反転して「右向き」を基準にする (2026-06-01: 左右逆の修正)。
  rig.skeleton.scaleX = -rig.scale
  rig.skeleton.scaleY = rig.scale
  // 揺れ (髪・スカートの physics 拘束・2026-10-04) は skeleton の時計で進む = 毎フレーム dt を渡す。
  //   体がゲームの中で動いた分 (歩き・踏み込み・弾かれ) は描く位置 (translate) で動かしているので骨には見えない
  //   → physicsTranslate で渡す (向きの反転と Y 下向きを skeleton の座標へ直す)。 向きが変わった時は揺れを消して立て直す。
  rig.skeleton.update(Math.max(0, dtSec) + takePhysicsExtra())
  let physics = Physics.update
  if (body) {
    const last = rig.lastBody
    if (!last || last.facing !== body.facing) physics = Physics.reset
    else rig.skeleton.physicsTranslate((body.x - last.x) * body.facing, -(body.y - last.y))
    rig.lastBody = { ...body }
  }
  // 4.3.5 では updateWorldTransform は Physics 引数が必須。
  // Physics.update = 2: 物理演算を更新してポーズに反映。
  rig.skeleton.updateWorldTransform(physics)

  // Spine の座標系は Y 上方向 + (足元 root から体が +Y へ伸びる)。
  // Canvas は Y 下方向 + なので、描画時に Y を反転して画面上向きに合わせる。
  // 呼び出し側で translate(足元) + scale(facing,1) 済みなので、ここでは Y のみ反転。
  ctx.save()
  ctx.scale(1, -1)
  rig.renderer.draw(rig.skeleton)
  ctx.restore()
  return true
}

/**
 * 直近に描いたコマのレイピアの根元と剣先 (rapier の骨の始点と、骨の長さぶん先)。
 *   座標は skeleton の世界座標 (Y 上向き・向きの反転は含まない)。 画面へは
 *   x = 足元x + 向き × x、 y = 足元y − y で直す (drawSpinePlayer と同じ変換)。 読み込み前は null。
 *   使うのは回避反撃の合図 (DEV ?counterLook=shadow・src/dodge_counter_look.ts) だけ。
 */
export function spinePlayerRapierPoints(): { grip: { x: number, y: number }, tip: { x: number, y: number } } | null {
  if (!rig) return null
  const bone = rig.skeleton.findBone('rapier')
  if (!bone) return null
  const w = bone.appliedPose
  // 骨の長さ (490) は刃の途中までしか無い。 絵の剣先は骨の向きに約 900 (rapier の絵 = 中心 386・長さ 2078×0.509 の半分 528 → 914。
  //   端の余白を引いて 900。 10-09 の撮影で、骨の長さを剣先にすると光が刃の真ん中に出ていた)。 護拳は骨の始点から少し先 (20)。
  const along = (d: number) => ({ x: w.worldX + w.a * d, y: w.worldY + w.c * d })
  return { grip: along(RAPIER_GUARD_ALONG), tip: along(RAPIER_TIP_ALONG) }
}
const RAPIER_GUARD_ALONG = 20
const RAPIER_TIP_ALONG = 900

/**
 * 直近に描いたコマの骨の上の点 (骨の始点から骨の向きに along 進んだ所)。 along = 'end' なら骨の長さの先。
 *   座標と向きは spinePlayerRapierPoints と同じ skeleton の世界座標 (Y 上向き・向きの反転は含まない)。
 *   dir = 骨の向きの単位ベクトル (同じ座標)。 読み込み前・骨が無ければ null。
 *   使うのは普段のパリィの見た目の比較版 (DEV ?parryLook=kesaki・src/parry_kesaki_fx.ts) だけ。
 */
export function spinePlayerBonePoint(name: string, along: number | 'end' = 0): { x: number, y: number, dx: number, dy: number } | null {
  if (!rig) return null
  const bone = rig.skeleton.findBone(name)
  if (!bone) return null
  const w = bone.appliedPose
  const d = along === 'end' ? bone.data.length : along
  const len = Math.hypot(w.a, w.c) || 1
  return { x: w.worldX + w.a * d, y: w.worldY + w.c * d, dx: w.a / len, dy: w.c / len }
}

/** スロットの絵 1枚を、描いたコマと同じ位置へ塗るための情報 (drawSpinePlayerSlotRegions が渡す)。 */
export interface SpineSlotRegionPaint {
  slot: string
  image: CanvasImageSource
  /** 絵の元画像の中の切り出し (px)。 */
  sx: number, sy: number, w: number, h: number
  /** 切り出しの上端が、余白を含めた元の絵の上から何 px か (髪なら上 = 根元・下 = 毛先)。 */
  rowTop: number
  originalHeight: number
}

/**
 * 直近に描いたコマの、名前を挙げたスロットの絵だけを、レンダラーと同じ変換で塗り直す。
 *   paint には「(0,0)〜(w,h) に元の絵の切り出しがそのまま重なる」座標の ctx が渡る (行 0 = 切り出しの上端)。
 *   呼び出し側は drawSpinePlayer と同じく ctx を足元原点へ translate・向きで scale 済みで渡すこと。
 *   回転して詰められた絵 (アトラスの rotate) は飛ばす (髪の3枚は回転していない)。 読み込み前は false。
 *   使うのは普段のパリィの見た目の比較版 (DEV ?parryLook=kesaki) だけ。 描画以外の状態は一切変えない。
 */
export function drawSpinePlayerSlotRegions(
  ctx: CanvasRenderingContext2D, slotNames: readonly string[],
  paint: (ctx: CanvasRenderingContext2D, region: SpineSlotRegionPaint) => void,
): boolean {
  if (!rig) return false
  const drawOrder = rig.skeleton.drawOrder.appliedPose
  ctx.save()
  ctx.scale(1, -1)
  for (const slot of drawOrder) {
    if (!slotNames.includes(slot.data.name) || !slot.bone.active) continue
    const pose = slot.appliedPose
    const attachment = pose.attachment
    if (!(attachment instanceof RegionAttachment)) continue
    const region = attachment.sequence.regions[attachment.sequence.resolveIndex(pose)]
    if (!region || region.degrees === 90) continue
    const image = region.texture.getImage() as CanvasImageSource & { width: number, height: number }
    // 以下は spine-canvas の SkeletonRenderer.drawImages と同じ変換 (4.3 系)。
    const b = slot.bone.appliedPose
    const offsets = attachment.getOffsets(pose)
    ctx.save()
    ctx.transform(b.a, b.c, b.b, b.d, b.worldX, b.worldY)
    ctx.translate(offsets[0], offsets[1])
    ctx.rotate(attachment.rotation * Math.PI / 180)
    const atlasScale = attachment.width / region.originalWidth
    ctx.scale(atlasScale * attachment.scaleX, atlasScale * attachment.scaleY)
    const w = region.width, h = region.height
    ctx.translate(w / 2, h / 2)
    ctx.scale(1, -1)
    ctx.translate(-w / 2, -h / 2)
    ctx.globalAlpha = 1
    paint(ctx, {
      slot: slot.data.name, image,
      sx: image.width * region.u, sy: image.height * region.v, w, h,
      rowTop: region.originalHeight - region.offsetY - h, originalHeight: region.originalHeight,
    })
    ctx.restore()
  }
  ctx.restore()
  return true
}

/**
 * 揺れ (髪・スカートの physics) へ「体がこれだけ動いた」を1回だけ足す (画面 px・向きは画面の向き)。
 *   体を接点の方へ押すと、髪は反対へ置いていかれて跳ね上がる。 描画の見た目だけ (ゲームの状態は変えない)。
 *   使うのは普段のパリィの見た目の比較版 (DEV ?parryLook=kesaki) だけ。
 */
let boneNudge: Record<string, number> | null = null
/**
 * 次に描くコマだけ、名指しの骨のアニメの回転へ度数を足す (揺れの計算の前)。 使うのは普段のパリィの見た目の比較版
 *   (DEV ?parryLook=kesaki) の「止めの間に髪が跳ね上がる」だけ。 呼ばなければ今のまま。
 */
export function nudgeSpinePlayerBones(deg: Record<string, number>): void { boneNudge = deg }
// ---- 歩きの比較版 (DEV ?lunaWalk=new|newslow・2026-10-12・src/player_walk_new.ts) だけが使う。 呼ばなければ何もしない ----
let animDtOverride: number | null = null
/** 次に描くコマだけ、アニメの時計をこの秒だけ進める (揺れの時計は描画の dt のまま)。 歩きの絵を進んだ距離で進めるため */
export function setNextSpinePlayerAnimDt(sec: number): void { animDtOverride = Math.max(0, sec) }
function takeAnimDtOverride(): number | null { const s = animDtOverride; animDtOverride = null; return s }
/** 止まる時の1歩: 足の的 (setup からの差・Spine の単位)・腰の上限・胴へ足す角度・効かせる重み */
export interface SpineLegOverride {
  front: { x: number, y: number }, back: { x: number, y: number }
  hipYMax: number, torsoAdd: number, weight: number
}
let legOverride: SpineLegOverride | null = null
/** 次に描くコマだけ、足の的・腰・胴をアニメの値から上書きする */
export function setNextSpinePlayerLegOverride(o: SpineLegOverride): void { legOverride = o }
function applyLegOverride(skeleton: Skeleton, o: SpineLegOverride): void {
  const w = Math.max(0, Math.min(1, o.weight))
  for (const [name, v] of [['leg_front_ik', o.front], ['leg_back_ik', o.back]] as const) {
    const b = skeleton.findBone(name)
    if (!b) continue
    const s = b.data.setupPose
    b.pose.x += (s.x + v.x - b.pose.x) * w
    b.pose.y += (s.y + v.y - b.pose.y) * w
  }
  const hip = skeleton.findBone('hip')
  if (hip) {
    const limit = hip.data.setupPose.y + o.hipYMax
    if (hip.pose.y > limit) hip.pose.y += (limit - hip.pose.y) * w
  }
  const torso = skeleton.findBone('torso')
  if (torso) torso.pose.rotation += o.torsoAdd * w
}
/** 今流しているアニメの名前とアニメの中の時刻 (秒)。 読み込み前は null */
export function spinePlayerTrack(): { name: string, time: number } | null {
  const e = rig?.state.getTrack(0)
  if (!e || !e.animation) return null
  return { name: e.animation.name, time: e.getAnimationTime() }
}
let physicsExtraSec = 0
/**
 * 次に描くコマだけ、揺れ (髪・スカートの physics) の時計を余分に進める (秒)。 体の動き (アニメ) は進めない。
 *   止め (ヒットストップ) の間はゲームの時計が止まって髪も止まるので、弾いた衝撃で髪だけが跳ね上がって見えるように使う。
 *   使うのは普段のパリィの見た目の比較版 (DEV ?parryLook=kesaki) だけ。 呼ばなければ 0 (今のまま)。
 */
export function addSpinePlayerPhysicsTime(sec: number): void { physicsExtraSec = Math.max(0, Math.min(0.05, sec)) }
function takePhysicsExtra(): number { const s = physicsExtraSec; physicsExtraSec = 0; return s }

export function kickSpinePlayerPhysics(dxScreen: number, dyScreen: number, facing: 1 | -1): void {
  if (!rig) return
  rig.skeleton.physicsTranslate(dxScreen * facing, -dyScreen)
}

/** attack のような非 loop アニメが終わったかの判定補助 (任意利用)。 */
export function isCurrentAnimComplete(): boolean {
  if (!rig) return false
  // 4.3.5 では getCurrent() は存在しない。getTrack(trackIndex) を使う。
  const entry = rig.state.getTrack(0)
  return entry ? entry.isComplete() : true
}

/**
 * dev 計測用: 直近フレームのトラック状態を返す。
 *   「姿は同じはずなのにカクッとする」の原因究明で、実際に何が再生され・何と混ざっているかを見る。
 */
export function debugSpineTrack(bones: string[] = ['rapier', 'arm_sword_hand', 'arm_sword_fore', 'arm_sword_upper', 'torso']): {
  anim: string | null, time: number, mixFrom: string | null, mixTime: number, mixDuration: number,
  bones: Record<string, { rot: number, x: number, y: number }>,
} | null {
  if (!rig) return null
  const e = rig.state.getTrack(0)
  if (!e) return null
  const out: Record<string, { rot: number, x: number, y: number }> = {}
  for (const name of bones) {
    const b = rig.skeleton.findBone(name)
    if (!b) continue
    // world 値は appliedPose 側にある (4.3 系)。 回転は骨の実際の向き = 絵の傾き。
    const w = b.appliedPose
    out[name] = {
      rot: +w.getWorldRotationX().toFixed(3),
      x: +w.worldX.toFixed(2), y: +w.worldY.toFixed(2),
    }
  }
  return {
    bones: out,
    anim: e.animation?.name ?? null,
    time: +e.trackTime.toFixed(4),
    mixFrom: (e.mixingFrom as { animation?: { name?: string } } | null)?.animation?.name ?? null,
    mixTime: +(e.mixTime ?? 0).toFixed(4),
    mixDuration: +(e.mixDuration ?? 0).toFixed(4),
  }
}
